/*======================================================================================================================
  * Bluebell: src/app/state/programmes/programmes.effects.ts
  * Last Edited: 10/01/2025
  * Copyright 2024-2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Dictionary } from '@ngrx/entity';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EffectHandlerService } from '@services/effect-handler.service';
import { updateSelectedAccount } from '@state/account/account.actions';
import { selectCurrentUserAccountSettings } from '@state/account/account.selectors';
import { ApiService } from '@state/api.service';
import { ProgrammeActions } from '@state/programmes/programme.actions';
import { Programme, ProgrammeUpdatePartial } from '@state/programmes/programme.interfaces';
import { selectCurrentProgrammeId, selectProgrammeEntities } from '@state/programmes/programme.selectors';
import { ProjectActions } from '@state/projects/project.actions';
import { FullProject } from '@state/projects/project.interfaces';
import { concatMap, mergeMap, of, switchMap } from 'rxjs';
import { filter, map } from 'rxjs/operators';


@Injectable()
export class ProgrammeEffects {
  private actions$ = inject(Actions);
  private api = inject(ApiService);
  private store = inject(Store);
  private handler = inject(EffectHandlerService);

  /*
      CRUD
  */

  selectedProgramme = this.store.selectSignal<string | null>(selectCurrentProgrammeId);

  create$ = createEffect(() => this.actions$.pipe(
    ofType(ProgrammeActions.create),
    mergeMap(({ data }) => this.api.createProgramme(data).pipe(
      this.handler.effectResponse({
        success: (programme: Programme) => ProgrammeActions.createSuccess(programme),
        successMessage: `Programme: ${data.name}, was created`,
        retry: ProgrammeActions.create(data),
        failure: ProgrammeActions.createFailure(),
        failureMessage: 'Could not create programme',
      }),
    )),
  ));

  loadAll$ = createEffect(() => this.actions$.pipe(
    ofType(ProgrammeActions.load, updateSelectedAccount),
    switchMap(() => this.api.listProgrammes().pipe(
      this.handler.effectResponse({
        success: (programmes: Programme[]) => ProgrammeActions.loadSuccess(programmes),
        retry: ProgrammeActions.load(),
        failure: ProgrammeActions.loadFailure(),
        failureMessage: 'Could not load programmes',
      }),
    )),
  ));

  update$ = createEffect(() => this.actions$.pipe(
    ofType(ProgrammeActions.update),
    concatMap((programmePartial: ProgrammeUpdatePartial) => this.api.updateProgramme(programmePartial).pipe(
      this.handler.effectResponse({
        success: (programme: Programme) => ProgrammeActions.updateSuccess(programme),
        successMessage: `Programme: ${programmePartial.name}, was updated`,
        retry: ProgrammeActions.update(programmePartial),
        failure: ProgrammeActions.updateFailure(),
        failureMessage: 'Could not update programme',
      }),
    )),
  ));

  delete$ = createEffect(() => this.actions$.pipe(
    ofType(ProgrammeActions.delete),
    mergeMap((programme: Programme) => this.api.deleteProgramme(programme.uuid).pipe(
      this.handler.effectResponse({
        success: () => ProgrammeActions.deleteSuccess(programme),
        retry: ProgrammeActions.delete(programme),
        failure: ProgrammeActions.deleteFailure(),
        failureMessage: 'Could not delete programme',
      }),
    )),
  ));

  /**
   * An effect to load all projects for a programme that has been selected in the user account settings in the Store
   * if such a programme exists.
   */

  loadProjectsFromSelectedProgramme$ = createEffect(() => this.actions$.pipe(
    ofType(ProgrammeActions.applyCurrentProgrammeFromUserSettings, updateSelectedAccount),
    concatLatestFrom(() => this.store.select(selectCurrentUserAccountSettings)),
    switchMap(([, userAccountSettings]) => {
      if (userAccountSettings?.current_programme) {
        this.store.dispatch(ProgrammeActions.applyCurrentProgrammeFromUserSettingsSuccess({
          uuid: userAccountSettings?.current_programme,
        }));
        return of(ProjectActions.load({
          project_table: true,
          selected_programme: userAccountSettings?.current_programme,
        }));
      } else {
        return of(ProgrammeActions.applyCurrentProgrammeFromUserSettingsFail());
      }
    }),
  ));

  /*
      Reacting to events outside Programmes Actions
  */

  onProjectSelect$ = createEffect(() => this.actions$.pipe(
    ofType(ProjectActions.selectSuccess),
    concatLatestFrom(() => this.store.select(selectProgrammeEntities)),
    filter(([project]: [FullProject, Dictionary<Programme>]): boolean => this.selectedProgramme() !== project.programme),
    map(([project, entities]: [FullProject, Dictionary<Programme>]) => ProgrammeActions.select(entities[project.programme] as Programme)),
  ));
}
